import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Download, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';

const REPORTS = [
  { id: 'revenue', label: 'Revenue (monthly)' },
  { id: 'payroll', label: 'Payroll' },
  { id: 'wallet', label: 'Wallet Transactions' },
  { id: 'marketplace', label: 'Marketplace Orders' },
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'refunds', label: 'Refunds' },
  { id: 'chargebacks', label: 'Chargebacks' },
  { id: 'transactions', label: 'Ledger Transactions' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'tax', label: 'Tax Summary (annual)' },
];

/** Phase 4 — centralized financial reports with filters, CSV export and print. */
export default function FinancialReportsPanel() {
  const [type, setType] = useState('revenue');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (status) p.set('status', status);
    return p.toString();
  }, [from, to, status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const d = await apiCall(`/api/finance/reports/${type}${qs ? `?${qs}` : ''}`);
      setData(d);
    } catch (e) {
      toast.error(e?.message || 'Failed to load report');
      setData(null);
    } finally { setLoading(false); }
  }, [type, buildQuery]);

  useEffect(() => { load(); }, [type]);  

  const exportCsv = async () => {
    try {
      const qs = buildQuery();
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/finance/reports/${type}?format=csv${qs ? `&${qs}` : ''}`,
        { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${type}_report.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    }
  };

  const printReport = () => {
    if (!data?.rows?.length) return;
    const w = window.open('', '_blank');
    const th = data.headers.map((h) => `<th style="text-align:left;padding:6px 10px;border-bottom:2px solid #333">${h}</th>`).join('');
    const trs = data.rows.map((r) => `<tr>${data.headers.map((h) => `<td style="padding:5px 10px;border-bottom:1px solid #eee">${r[h] ?? ''}</td>`).join('')}</tr>`).join('');
    w.document.write(`<html><head><title>${type} report — ScensobConnect</title></head><body style="font-family:system-ui;font-size:12px">
      <h2>ScensobConnect — ${REPORTS.find((x) => x.id === type)?.label || type}</h2>
      <p>Generated ${new Date().toLocaleString()} · ${data.count} rows</p>
      <table style="border-collapse:collapse;width:100%"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>
    </body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200" data-testid="financial-reports-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mr-2">
          <FileText className="w-4 h-4 text-indigo-600" /> Financial Reports
        </h4>
        <select value={type} onChange={(e) => setType(e.target.value)}
          className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="report-type-select">
          {REPORTS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="report-from-input" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="report-to-input" />
        <input placeholder="Status filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="w-28 px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="report-status-input" />
        <button onClick={load} disabled={loading}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
          data-testid="report-run-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run'}
        </button>
        <div className="ml-auto flex gap-2">
          <button onClick={exportCsv} disabled={!data?.rows?.length}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            data-testid="report-export-csv-btn">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button onClick={printReport} disabled={!data?.rows?.length}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            data-testid="report-print-btn">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto" data-testid="report-table">
        {!data?.rows?.length ? (
          <p className="px-4 py-8 text-xs text-gray-400 text-center">{loading ? 'Loading…' : 'No rows for the selected filters.'}</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50">
              <tr>{data.headers.map((h) => <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.rows.slice(0, 200).map((r, i) => (
                <tr key={r.id || i} className="hover:bg-gray-50/60">
                  {data.headers.map((h) => (
                    <td key={h} className="px-3 py-1.5 text-gray-700 whitespace-nowrap max-w-[220px] truncate">
                      {typeof r[h] === 'number' ? r[h].toLocaleString() : String(r[h] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data?.count > 200 && (
        <p className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100">Showing first 200 of {data.count} rows — export CSV for the full set.</p>
      )}
    </div>
  );
}
